import Observable, { merge as mergeObservables } from '@/utils/Observable'
import km, { listenRealtimeEvents } from '@/utils/pm2Io'
import { COLLECT_LOGS } from '@/store/actions'
export const TRIGGER_CUSTOM_ACTION = 'TRIGGER_CUSTOM_ACTION'
export const TRIGGER_CUSTOM_SCOPED_ACTION = 'TRIGGER_CUSTOM_SCOPED_ACTION'
export const TRIGGER_PM2_ACTION = 'TRIGGER_PM2_ACTION'
export const ACTION_STATUS_SENDING = 1
export const ACTION_STATUS_RUNNING = 2
export const ACTION_STATUS_SUCCEEDED = 3
export const ACTION_STATUS_FAILED = 4

async function followLogs(
  dispatch,
  actionObservable,
  { bucketId, serverName, appName, processId },
) {
  const logsObservable = await dispatch(COLLECT_LOGS, { bucketId, serverName, appName })

  return mergeObservables(
    logsObservable
      .filter(logEntry =>
        logEntry.process.name === appName &&
        logEntry.process.server === serverName &&
        logEntry.process.pm_id === processId,
      )
      .map(logEntry => createEvent('log', logEntry.line)),
    actionObservable,
  )
}

function createEvent(kind, response, status) {
  if (typeof response !== 'string') {
    response = JSON.stringify(response, null, 2)
  }
  if (!response.endsWith('\n')) {
    response = response + '\n'
  }
  response = response.replace(/<br\s*\/?>/gi, '\n')
  return { kind, response, status }
}

function formatError(error) {
  return error.message || (error.data && (error.data.msg || error.data.cause)) || error
}

const actions = {
  async [TRIGGER_CUSTOM_ACTION](
    { commit, getters, dispatch },
    {
      bucketId = getters.currentBucket.bucket.id,
      processId,
      serverName,
      actionName,
      appName,
      opts,
    },
  ) {
    let actionObservable

    const remoteCmd = {
      process_id: Number(processId),
      server_name: serverName,
      action_name: actionName,
      opts,
    }

    actionObservable = new Observable(observer => {
      km.actions.triggerAction(bucketId, remoteCmd).then(
        () => {
          observer.next(createEvent('info', 'Action started\n', ACTION_STATUS_RUNNING))
        },
        error => {
          observer.next(createEvent('error', formatError(error), ACTION_STATUS_FAILED))
          observer.complete()
        },
      )

      return listenRealtimeEvents(getters.currentBucket.bucket, {
        '*:axm:reply'(res) {
          const replyMessage = res.find(message => {
            return (
              message.data.action_name === actionName &&
              message.process.pm_id === processId &&
              message.process.server === serverName
            )
          })
          if (replyMessage) {
            observer.next(
              createEvent('result', replyMessage.data.return, ACTION_STATUS_SUCCEEDED),
            )
            observer.next(createEvent('info', 'Action completed'))
            observer.complete()
          }
        },
      })
    })

    return followLogs(dispatch, actionObservable, {
      bucketId,
      serverName,
      appName,
      processId,
    })
  },
  async [TRIGGER_CUSTOM_SCOPED_ACTION](
    { commit, dispatch, getters },
    {
      bucketId = getters.currentBucket.bucket.id,
      processId,
      serverName,
      actionName,
      appName,
      opts,
    },
  ) {
    const remoteCmd = {
      pm_id: Number(processId),
      server_name: serverName,
      action_name: actionName,
      app_name: appName,
      opts,
    }

    const actionObservable = new Observable(observer => {
      km.actions.triggerScopedAction(bucketId, remoteCmd).then(
        () => {
          observer.next(createEvent('info', 'Action started', ACTION_STATUS_RUNNING))
        },
        error => {
          observer.next(createEvent('error', formatError(error), ACTION_STATUS_FAILED))
          observer.complete()
        },
      )

      function filterReleventMessages(messages) {
        return messages.filter(message => {
          return (
            message.data.action_name === actionName &&
            message.process.pm_id === processId &&
            message.process.server === serverName
          )
        })
      }

      return listenRealtimeEvents(getters.currentBucket.bucket, {
        '*:axm:scoped_action:stream'(res) {
          filterReleventMessages(res).forEach(message => {
            observer.next(createEvent('result', message.data.data))
          })
        },
        '*:axm:scoped_action:error'(res) {
          filterReleventMessages(res).forEach(message => {
            observer.next(createEvent('error', message.data.data, ACTION_STATUS_FAILED))
            observer.complete()
          })
        },
        '*:axm:scoped_action:end'(res) {
          filterReleventMessages(res).forEach(message => {
            observer.next(createEvent('info', 'Action completed', ACTION_STATUS_SUCCEEDED))
            observer.complete()
          })
        },
      })
    })

    return followLogs(dispatch, actionObservable, { bucketId, serverName, appName, processId })
  },
  [TRIGGER_PM2_ACTION](
    { commit, dispatch, getters },
    {
      bucketId = getters.currentBucket.bucket.id,
      processId,
      actionName,
      serverName,
      appName,
    },
  ) {
    let actionObservable

    const remoteCmd = {
      app_name: processId !== undefined ? String(processId) : appName,
      server_name: serverName,
      method_name: actionName,
    }

    actionObservable = new Observable(async observer => {
      try {
        observer.next(createEvent('info', `Action ${actionName} started`, ACTION_STATUS_SENDING))
        await km.actions.triggerPM2Action(bucketId, remoteCmd)
        observer.next(
          createEvent('info', `Action ${actionName} completed`, ACTION_STATUS_SUCCEEDED),
        )
      } catch (error) {
        observer.next(createEvent('error', formatError(error), ACTION_STATUS_FAILED))
      }
      observer.complete()
    })

    return followLogs(dispatch, actionObservable, {
      bucketId,
      serverName,
      appName,
      processId,
    })
  },
}

export default {
  actions,
}
